import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { z } from 'zod';

import {
  AUTH_API_PATH,
  HTTPS_PROTOCOL,
  parseOrReturnError,
  restAPI,
} from '../rest-api';
import { TenantService } from '../tenant/endpoints';
import { UserTenant } from '../tenant/types';

import { ROLE_VALUES } from '~/constants/customerConstant';

const ephermalResponseSchema = z.object({
  ephemeral_token: z.string(),
  method: z.enum(['app', 'email']),
});
type EphpermalResponse = z.infer<typeof ephermalResponseSchema>;

const regionResponseSchema = z.object({
  message: z.string(),
  user: z.object({
    id: z.number(),
    username: z.string(),
    region: z.string(),
    region_name: z.string(),
    tenant: z.string(),
    tenant_name: z.string(),
  }),
});

type RegionResponse = z.infer<typeof regionResponseSchema>;

const regionTenantResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  region: z.string(),
  region_name: z.string(),
});

type RegionTenantResponse = z.infer<typeof regionTenantResponseSchema>;

export const authenticatedResponseSchema = z.object({
  user_type: z.number(),
  access_expiration: z.coerce.string().datetime(),
  refresh_expiration: z.coerce.string().datetime(),
  ws_access: z.string().catch(''),
  ws_refresh: z.string().catch(''),
  googleAuthAccountFlag: z.boolean().optional(),
  is_google_oauth_only: z.boolean().optional().catch(false),
});
type AuthenticatedResponse = z.infer<typeof authenticatedResponseSchema>;

export const registeredWhistleblowerResponseSchema = z.object({
  access: z.coerce.string().datetime(),
  refresh: z.coerce.string().datetime(),
  ws_access: z.string().catch(''),
  ws_refresh: z.string().catch(''),
});
type RegisteredWhistleblowerResponse = z.infer<
  typeof registeredWhistleblowerResponseSchema
>;

const credentialsResponseSchema = z
  .object({
    slug: z.string(),
    has_onboarding_done: z.boolean(),
  })
  .extend(authenticatedResponseSchema.shape);
type CredentialDetailsResponse = z.infer<typeof credentialsResponseSchema>;

type LoginResponseData =
  | {
      ephermal: EphpermalResponse;
      credentials?: never;
    }
  | { ephermal?: never; credentials: CredentialDetailsResponse };

export const rfiRecipientLoginResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
});

type RfiRecipientLoginResponseData = z.infer<
  typeof rfiRecipientLoginResponseSchema
>;

const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    // Check Region
    checkRegion: builder.mutation<RegionResponse, { username: string }>({
      query: ({ username }) => ({
        url: '/check-region',
        method: 'POST',
        data: { username },
        baseURLOverride: `${HTTPS_PROTOCOL}${AUTH_API_PATH}`,
      }),
    }),
    // Check Region of Tenant for Public Pages
    checkTenant: builder.query<RegionTenantResponse, { tenant: string }>({
      query: ({ tenant }) => ({
        url: `/tenant/${tenant}`,
        method: 'GET',
        baseURLOverride: `${HTTPS_PROTOCOL}${AUTH_API_PATH}`,
      }),
    }),
    // Customer Login
    loginWithCredentials: builder.mutation<
      LoginResponseData,
      { username: string; password: string }
    >({
      queryFn: async (
        { username, password },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: '/tenant/login',
          method: 'POST',
          data: {
            username: username,
            password: password,
            is_http_cookie_only: true,
          },
        });
        if (result.error) {
          return { error: result.error };
        }
        const ephermalData = ephermalResponseSchema.safeParse(result.data);
        // Return ephermal token to proceed with OTP
        if (ephermalData.success) {
          return { data: { ephermal: ephermalData.data } };
        }

        // The API should return tokens if OTP is not enabled
        const tenantResult = await baseQuery({
          url: '/tenant',
          withCredentials: true,
        });
        const onboarded = await baseQuery({
          url: '/tenant/quickstart',
          withCredentials: true,
        });
        const error = tenantResult.error;
        if (error) {
          return { error };
        }
        const tenant = credentialsResponseSchema.safeParse({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(result.data as unknown as Record<string, any>),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(tenantResult.data as unknown as Record<string, any>),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(onboarded.data === undefined ? { has_onboarding_done: true } : onboarded.data as unknown as Record<string, any>),
        });

        if (!tenant.success) {
          return {
            error: {
              status: 404,
              data: { message: 'Tenant not found for account' },
            } as FetchBaseQueryError,
          };
        }

        return {
          data: {
            credentials: tenant.data,
            // ...(result.data as Record<string, any>),
          },
        };
      },
      extraOptions: { requireAuth: true },
    }),
    loginWithKey: builder.mutation<
      AuthenticatedResponse,
      { referenceKey: string }
    >({
      query: ({ referenceKey }) => ({
        url: '/login/reference-key',
        method: 'POST',
        data: {
          reference_key: referenceKey,
          is_http_cookie_only: true,
        },
      }),
    }),
    loginWithCode: builder.mutation<
      AuthenticatedResponse & Pick<UserTenant, 'slug'>,
      { ephemeralToken: string; code: string }
    >({
      async queryFn({ ephemeralToken, code }, api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/tenant/login/code',
          method: 'POST',
          data: {
            ephemeral_token: ephemeralToken,
            code,
            is_http_cookie_only: true,
          },
        });
        const otpVerificationResponse = authenticatedResponseSchema.safeParse(
          result.data,
        );
        if (result.error || !otpVerificationResponse.success) {
          const error = result.error ?? {
            status: 400,
            data: { message: 'Invalid Response Shape' },
          };
          return { error };
        }
        const tenant = await api.dispatch(
          TenantService.endpoints.userTenant.initiate(),
        );

        if (tenant.isError) {
          return { error: tenant.error as FetchBaseQueryError };
        }

        return {
          data: {
            ...otpVerificationResponse.data,
            slug: tenant.data?.slug || '',
          },
        };
      },
      extraOptions: { requireAuth: true },
    }),
    loginWithAuthenticatorCode: builder.mutation<
      AuthenticatedResponse & Pick<UserTenant, 'slug'>,
      { ephemeralToken: string; code: string }
    >({
      async queryFn({ ephemeralToken, code }, api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/login/code',
          method: 'POST',
          data: {
            ephemeral_token: ephemeralToken,
            code,
            is_http_cookie_only: true,
          },
        });

        const otpVerificationResponse = authenticatedResponseSchema.safeParse(
          result.data,
        );
        if (result.error || !otpVerificationResponse.success) {
          const error = result.error ?? {
            status: 400,
            data: { message: 'Invalid Response Shape' },
          };
          return { error };
        }

        let slug = '';
        // We only retrieve the slug if the user is not a whistleblower
        if (
          otpVerificationResponse.data.user_type !== ROLE_VALUES.WHISTLE_BLOWER
        ) {
          const tenant = await api.dispatch(
            TenantService.endpoints.userTenant.initiate(),
          );

          if (tenant.isError) {
            return { error: tenant.error as FetchBaseQueryError };
          }
          slug = tenant.data?.slug ?? '';
        }

        return {
          data: {
            ...otpVerificationResponse.data,
            slug,
          },
        };
      },
      extraOptions: { requireAuth: true },
    }),
    loginWithGoogle: builder.mutation<AuthenticatedResponse, { code: string }>({
      query: (payload) => ({
        url: '/tenant/login/google',
        method: 'POST',
        data: { ...payload, is_http_cookie_only: true },
      }),
    }),
    logout: builder.mutation<boolean, void>({
      query: () => ({
        url: '/logout',
        method: 'POST',
        data: { is_http_cookie_only: true },
      }),
    }),
    //Customer Register
    registerCustomer: builder.mutation<
      AuthenticatedResponse & Pick<UserTenant, 'slug'>,
      { email: string; password: string }
    >({
      async queryFn({ email, password }, api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/tenant/register',
          method: 'POST',
          data: {
            username: email,
            password: password,
            is_http_cookie_only: true,
          },
        });
        if (result.error) {
          return { error: result.error };
        }
        const tenant = await api.dispatch(
          TenantService.endpoints.userTenant.initiate(),
        );
        if (tenant.isError) {
          return { error: tenant.error as FetchBaseQueryError };
        }

        return parseOrReturnError(() => {
          const tokens = authenticatedResponseSchema.parse(result.data);
          return { ...tokens, slug: tenant.data?.slug || '' };
        });
      },
    }),

    //Whistlblower Auth
    loginWhistleblowerWithCredentials: builder.mutation<
      LoginResponseData,
      { username: string; password: string }
    >({
      queryFn: async (
        { username, password },
        _api,
        _extraOptions,
        baseQuery,
      ) => {
        const result = await baseQuery({
          url: '/login',
          method: 'POST',
          data: {
            username: username,
            password: password,
            is_http_cookie_only: true,
          },
        });
        if (result.error) {
          return { error: result.error };
        }
        const ephermalData = ephermalResponseSchema.safeParse(result.data);
        if (ephermalData.success) {
          return { data: { ephermal: ephermalData.data } };
        }

        const credentials = credentialsResponseSchema
          .pick({
            user_type: true,
            ws_access: true,
            ws_refresh: true,
            access_expiration: true,
            refresh_expiration: true,
          })
          .safeParse({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...(result.data as unknown as Record<string, any>),
          });

        if (!credentials.success) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Failed to parse the response data',
            },
          };
        }

        return {
          data: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            credentials: credentials.data as any,
          },
        };
      },
      extraOptions: { requireAuth: true },
    }),
    registerWhistleblower: builder.mutation<
      RegisteredWhistleblowerResponse,
      {
        payload:
          | { email: string; password: string; invitationKey?: string }
          | { email?: never; password?: never; invitationKey: string };
      }
    >({
      query: (arg) => {
        const payload = () => {
          if (arg.payload.email && arg.payload.password) {
            return {
              username: arg.payload.email,
              password: arg.payload.password,
              report: arg.payload.invitationKey,
              is_http_cookie_only: true,
            };
          }
          return {
            report: arg.payload.invitationKey,
            is_http_cookie_only: true,
          };
        };

        return {
          url: '/register',
          method: 'POST',
          data: payload(),
        };
      },
    }),
    registerViaPrivateKey: builder.mutation<
      AuthenticatedResponse & { draft_report: string },
      {
        private_key: string;
        tenantSlug: string;
      }
    >({
      query: ({ tenantSlug, ...payload }) => {
        return {
          url: `/${tenantSlug}/register/private-key`,
          method: 'POST',
          data: { ...payload, is_http_cookie_only: true },
        };
      },
    }),
    verifyInvitationCode: builder.mutation<void, { invitationCode: string }>({
      query({ invitationCode }) {
        return { url: `/validate-link/${invitationCode}/invite-link` };
      },
      extraOptions: {
        requireAuth: false,
      },
    }),
    // Resend OTP Code
    resendOtpCode: builder.mutation<void, { ephemeralToken: string }>({
      query({ ephemeralToken }) {
        return {
          url: '/tenant/login/resend-code',
          method: 'POST',
          data: {
            ephemeral_token: ephemeralToken,
            for_resending: true,
          },
        };
      },
      extraOptions: { requireAuth: false },
    }),

    sendResetPasswordEmail: builder.mutation<
      void,
      { email: string; slug?: string }
    >({
      query: ({ email, slug }) => ({
        url: slug
          ? `/${slug}/send-reset-password-email`
          : '/send-reset-password-email',
        method: 'POST',
        data: { email },
      }),
    }),
    // Reset password for a user
    customerResetPassword: builder.mutation<
      void,
      { resetToken: string; uid: string; password: string }
    >({
      query: ({ resetToken, uid, password }) => ({
        url: `/tenant/${uid}/${resetToken}/reset-password`,
        method: 'POST',
        data: { password },
      }),
      extraOptions: { requireAuth: false },
    }),
    loginRfiRecipient: builder.mutation<
      RfiRecipientLoginResponseData,
      { code: string }
    >({
      query: ({ code }) => ({
        url: '/login-rfi-recipient',
        method: 'POST',
        data: { external_token: code },
      }),
      extraOptions: { requireAuth: false },
    }),
    generatePrivateKey: builder.mutation<{ private_key: string }, void>({
      query: () => ({ url: '/private-key', method: 'GET' }),
    }),
  }),
  overrideExisting: false,
});

export const AuthService = endpoints;
